import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormHelperText, Grid, TextField, Typography, Stack, Tooltip } from '@vapor/material'
import { defaultText, Language, LocalizedString, MultiLanguagesDialogProps } from './multi-languages-types'
import { ChangeEvent, Fragment, useCallback, useEffect, useState } from 'react'
import { MultiLanguagesButton } from './multi-languages-commons'
import useConfirm from '../confirm/confirm-hook'

/** Input per la gestione di stringhe localizzate */
const MultiLanguagesDialog = (props: MultiLanguagesDialogProps) => {
  const { open, handleClose, handleConfirm, languages, mainLanguage, label, value, pendingForTranslation: pendingForTransaltion, multiline, error } = props
  const i18n = { ...defaultText, ...props.i18n }

  const [internalValue, setInternalValue] = useState<LocalizedString | null>(null)

  useEffect(() => {
    if (value) setInternalValue([...value])
  }, [value])

  const handleOnChange = (lng: Language, e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setInternalValue((t) => {
      let changedValue: LocalizedString | null = null
      if (t) {
        changedValue = [...t.filter((x) => x.language !== lng), { language: lng, value: e.target.value }].filter((i) => i.value)
      } else if (e.target.value) {
        changedValue = [{ language: lng, value: e.target.value }]
      }
      changedValue?.sort((a) => (a.language === mainLanguage ? -1 : 0))
      return changedValue?.length ? changedValue : null
    })
  }

  const handleCancel = useCallback(() => {
    if (value) setInternalValue(value)
    handleClose()
  }, [value])

  const handleOnConfirm = useCallback(() => {
    handleConfirm(internalValue)
    handleClose()
  }, [internalValue])

  const confirm = useConfirm()

  const handleExtTranslation = useCallback(() => {
    confirm({
      title: i18n['message.override_translations_title'],
      message: `"${label}" ${i18n['message.override_translations']}`,
      confirmButtonText: i18n['buttons.confirm'],
      cancelButtonText: i18n['buttons.cancel'],
      onConfirm: () => {
        if (props.handleExtTranslation && internalValue && internalValue.length) {
          setInternalValue([...props.handleExtTranslation(languages, internalValue)])
        }
      },
    })
  }, [props.handleExtTranslation, internalValue, label])

  const getLanguageValue = useCallback(
    (language: Language) => {
      let result = ''
      if (internalValue && internalValue.length) result = internalValue?.find((l) => l.language === language)?.value ?? ''
      return result
    },
    [internalValue],
  )

  return (
    <Dialog open={open} onClose={handleCancel} fullWidth>
      <DialogTitle sx={{ borderBottom: '1px solid #ccc', mb: 1, pb: 1, boxShadow: 4 }}>
        <Stack width={'100%'} pr={1}>
          <Grid container spacing={1} sx={{ pb: 1, pt: 2 }} alignItems="center">
            <Grid item xs={0} sm={1}></Grid>
            <Grid item xs={12} sm={9}>
              <Typography variant="label">{label}</Typography>
            </Grid>
            <Grid item xs={12} sm={2} display="flex" justifyContent="flex-end">
              <MultiLanguagesButton loading={pendingForTransaltion ?? false} handleOnClick={handleExtTranslation} />
            </Grid>
          </Grid>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={1}>
              <Typography variant="body2">{mainLanguage.toUpperCase()}</Typography>
            </Grid>
            <Grid item xs={12} sm={11}>
              <TextField type="outlined" fullWidth value={getLanguageValue(mainLanguage)} onChange={(e) => handleOnChange(mainLanguage, e)} multiline={multiline} error={error !== undefined} />
              <Grid item xs={12} sm={11}>
                {error && (
                  <FormHelperText id="multilanguage-input-error" error={error !== undefined}>
                    {error}
                  </FormHelperText>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ overflowY: 'overlay' }}>
        <Grid container spacing={1}>
          {languages.map((lng, key) => {
            if (lng !== mainLanguage)
              return (
                <Fragment key={key}>
                  <Grid item xs={12} sm={1}>
                    <Typography variant="body2">{lng.toUpperCase()}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={11}>
                    <Tooltip title={`${i18n?.['multilanguage.input.other_languages_tooltip']} ${lng.toUpperCase()}`}>
                      <TextField type="outlined" fullWidth value={getLanguageValue(lng)} onChange={(e) => handleOnChange(lng, e)} multiline={multiline} />
                    </Tooltip>
                  </Grid>
                </Fragment>
              )
          })}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ boxShadow: 4 }}>
        <Grid container sx={{ p: 2, pt: 0 }}>
          <Grid item display="flex" justifyContent="flex-start" xs={6}>
            <Button variant="outlined" onClick={handleCancel}>
              {i18n?.['buttons.cancel']}
            </Button>
          </Grid>
          <Grid item display="flex" justifyContent={'flex-end'} xs={6}>
            <Button variant="contained" onClick={handleOnConfirm}>
              {i18n?.['buttons.confirm']}
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  )
}

export default MultiLanguagesDialog
